import {
  Card,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { ChevronDown, Clock3 } from 'lucide-react';
import { useMemo } from 'react';
import { useSpectraChartData } from '../hooks/useSpectraChartData';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import { formatSegmentTabLabel } from '../utils/customSegment';
import { CustomSegmentManager } from './CustomSegmentManager';
import { SpectraPsdChart } from './SpectraPsdChart';

const SPECTRA_SECTION_TITLE = 'PSD tachogramma NN';

const SPECTRA_SECTION_CAPTION =
  'Welch sulla serie detrendata e ricampionata a 4 Hz (HRV preprocessing, tachogramma NN da HR). Il rapporto LF/HF deriva dall’integrazione nelle bande su questa PSD, non dai canali lf/hf del Polar. Vedi guida utente § 11.6.';

type ZoomSelectsProps = {
  startOptions: Array<{ value: string; label: string }>;
  endOptions: Array<{ value: string; label: string }>;
  startValue: string | null;
  endValue: string | null;
  onStartChange: (value: string | null) => void;
  onEndChange: (value: string | null) => void;
  availableSeconds: number[];
  disabled?: boolean;
};

function ZoomSelects({
  startOptions,
  endOptions,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  availableSeconds,
  disabled = false,
}: ZoomSelectsProps) {
  return (
    <Group gap='md' align='flex-end' wrap='wrap'>
      <Select
        label='Inizio'
        placeholder='Seleziona timestamp inizio'
        variant='filled'
        leftSection={<Clock3 size={14} />}
        rightSection={<ChevronDown size={14} />}
        data={startOptions}
        value={startValue}
        disabled={disabled}
        onChange={value => {
          if (!value) {
            onStartChange(null);
            return;
          }
          onStartChange(value);
          if (endValue && Number(value) >= Number(endValue)) {
            const nextSecond = availableSeconds.find(
              second => second > Number(value)
            );
            onEndChange(nextSecond != null ? nextSecond.toString() : null);
          }
        }}
        clearable
        w={220}
      />
      <Select
        label='Fine'
        placeholder='Seleziona timestamp fine'
        variant='filled'
        leftSection={<Clock3 size={14} />}
        rightSection={<ChevronDown size={14} />}
        data={endOptions}
        value={endValue}
        disabled={disabled}
        onChange={onEndChange}
        clearable
        w={220}
      />
    </Group>
  );
}

type SpectraSectionProps = {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  onActiveViewChange: (viewId: ChartViewId) => void;
  customSegments: CustomTimeSegment[];
  onCustomSegmentsChange: (segments: CustomTimeSegment[]) => void;
  trendStart: string | null;
  trendEnd: string | null;
  onTrendStartChange: (value: string | null) => void;
  onTrendEndChange: (value: string | null) => void;
  selectedSessionId: string | null;
  onSelectedSessionChange: (value: string | null) => void;
  logScale: boolean;
  onLogScaleChange: (value: boolean) => void;
  spectraStart: string | null;
  spectraEnd: string | null;
  onSpectraStartChange: (value: string | null) => void;
  onSpectraEndChange: (value: string | null) => void;
  computeEnabled?: boolean;
};

export function SpectraSection({
  readySessions,
  activeViewId,
  onActiveViewChange,
  customSegments,
  onCustomSegmentsChange,
  trendStart,
  trendEnd,
  onTrendStartChange,
  onTrendEndChange,
  selectedSessionId,
  onSelectedSessionChange,
  logScale,
  onLogScaleChange,
  spectraStart,
  spectraEnd,
  onSpectraStartChange,
  onSpectraEndChange,
  computeEnabled = true,
}: SpectraSectionProps) {
  const chartData = useSpectraChartData({
    readySessions,
    activeViewId,
    customSegments,
    trendStart,
    trendEnd,
    selectedSessionId,
    logScale,
    spectraStart,
    spectraEnd,
    computeEnabled,
  });

  const isBusy = chartData.isSpectraComputing;

  const activeSegment = customSegments.find(
    segment => segment.id === activeViewId
  );

  const chartTitle = useMemo(() => {
    if (activeViewId === 'full') return 'Tratto completo';
    if (activeSegment) return formatSegmentTabLabel(activeSegment);
    return 'Tratto custom';
  }, [activeSegment, activeViewId]);

  if (readySessions.length === 0) {
    return (
      <Card withBorder>
        <Text c='dimmed' size='sm'>
          Aggiungi almeno una sessione con dati HR per visualizzare gli spettri.
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap='md'>
      <Card withBorder>
        <Group justify='space-between' align='flex-start' wrap='wrap'>
          <Stack gap={4} maw={640}>
            <Title order={4}>{SPECTRA_SECTION_TITLE}</Title>
            <Text size='sm' c='dimmed'>
              {SPECTRA_SECTION_CAPTION}
            </Text>
          </Stack>
          <Group>
            <Switch
              checked={logScale}
              disabled={isBusy}
              onChange={event => onLogScaleChange(event.currentTarget.checked)}
              label='Scala log'
            />
            <Select
              placeholder='Media gruppo'
              clearable
              disabled={isBusy}
              data={chartData.sessionSelectData}
              value={selectedSessionId}
              onChange={onSelectedSessionChange}
              w={240}
            />
          </Group>
        </Group>

        <Group gap='md' align='flex-end' wrap='wrap' mt='md'>
          <Select
            label='Inizio (Hz)'
            placeholder='Frequenza inizio'
            disabled={isBusy}
            data={chartData.spectraStartOptions}
            value={spectraStart}
            onChange={value => {
              if (!value) {
                onSpectraStartChange(null);
                return;
              }

              onSpectraStartChange(value);

              if (spectraEnd && Number(value) >= Number(spectraEnd)) {
                const nextFrequency = chartData.availableFrequencies?.find(
                  frequency => frequency > Number(value)
                );
                onSpectraEndChange(
                  nextFrequency != null ? nextFrequency.toString() : null
                );
              }
            }}
            clearable
            w={220}
          />
          <Select
            label='Fine (Hz)'
            placeholder='Frequenza fine'
            disabled={isBusy}
            data={chartData.spectraEndOptions}
            value={spectraEnd}
            onChange={onSpectraEndChange}
            clearable
            w={220}
          />
        </Group>
      </Card>

      <div
        style={{
          opacity: isBusy ? 0.6 : 1,
          pointerEvents: isBusy ? 'none' : undefined,
        }}
      >
        <CustomSegmentManager
          activeViewId={activeViewId}
          onActiveViewChange={onActiveViewChange}
          customSegments={customSegments}
          onCustomSegmentsChange={onCustomSegmentsChange}
          availableSeconds={chartData.availableSeconds}
          maxAvailableSecond={chartData.maxAvailableSecond}
        />
      </div>

      <Card withBorder pos='relative'>
        <LoadingOverlay
          visible={isBusy}
          zIndex={100}
          overlayProps={{ radius: 'sm', blur: 2 }}
          loaderProps={{ type: 'bars' }}
        />
        <Stack gap='md'>
          <Title order={5}>{chartTitle}</Title>

          {activeViewId === 'full' && (
            <ZoomSelects
              startOptions={chartData.trendStartOptions}
              endOptions={chartData.trendEndOptions}
              startValue={trendStart}
              endValue={trendEnd}
              onStartChange={onTrendStartChange}
              onEndChange={onTrendEndChange}
              availableSeconds={chartData.availableSeconds}
              disabled={isBusy}
            />
          )}

          {isBusy && chartData.psdChartData.length === 0 ? (
            <Text size='sm' c='dimmed'>
              Calcolo PSD in corso…
            </Text>
          ) : chartData.psdChartData.length > 0 ? (
            <SpectraPsdChart
              data={chartData.psdChartData}
              logScale={logScale}
            />
          ) : (
            <Text size='sm' c='dimmed'>
              Nessun dato PSD disponibile per il tratto selezionato.
            </Text>
          )}

          {chartData.includedSessionCount > 0 && !selectedSessionId && (
            <Text size='sm' c='dimmed'>
              Media gruppo su {chartData.includedSessionCount} sessioni nel
              tratto.
            </Text>
          )}

          {chartData.excludedSessions.length > 0 && (
            <Stack gap={4}>
              <Text size='sm' fw={600}>
                Sessioni escluse dal calcolo
              </Text>
              {chartData.excludedSessions.map(entry => (
                <Text key={entry.sessionId} size='sm' c='dimmed'>
                  {entry.sessionId}: {entry.reason}
                </Text>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
